import { TPTaxReportContent, TPTaxReportData } from 'src/models/reports/TPTaxReport';
import {
    LefDrillDownDimension,
    LEF_DRILLDOWN_DIMENSIONS,
    LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID,
} from 'src/utils/constants';

/**
 * Pure helpers behind the LEF segment drill-down (Epic 1). Deliberately report-agnostic and free of
 * React so MAR and CWB can reuse them: everything here takes rows plus a dimension and returns rows,
 * labels or booleans.
 *
 * Shape of a segment row, confirmed against the sample payload:
 *   attributeNameHash    `A6000$Cost Center$C120`  → `<parentAccount>$<dimensionLabel>$<segmentCode>`
 *   attributeParentNameHash  the parent segment, or `Account$A6000` for the FRAMES root
 *   displayName          `C120  FUL - Operations`  → code, two-or-more spaces, description
 * Section rows (`Revenue`, `OPEX`, ...) and account rows (`Account$A4000`) carry neither the `$` triple
 * nor, for sections, a code — they are repeated verbatim in every dimension view.
 */

/** Shown in place of a segment description when the payload gives a code with no description. */
export const SEGMENT_DESCRIPTION_UNAVAILABLE = 'Description unavailable';

export const ACCOUNT_DIMENSION: LefDrillDownDimension = LEF_DRILLDOWN_DIMENSIONS.find(
    (dimension) => dimension.id === LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID
) as LefDrillDownDimension;

/** Resolves a selector id to its dimension, falling back to Account for an unknown id. */
export function getDrillDownDimension(dimensionId: string | undefined | null): LefDrillDownDimension {
    return LEF_DRILLDOWN_DIMENSIONS.find((dimension) => dimension.id === dimensionId) ?? ACCOUNT_DIMENSION;
}

export function isAccountDimension(dimension: LefDrillDownDimension): boolean {
    return dimension.id === LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID;
}

/** True when the row belongs to the dimension's FRAMES hierarchy rather than the account hierarchy. */
export function isSegmentRow(row: TPTaxReportData, dimension: LefDrillDownDimension): boolean {
    return !!dimension.dimensionLabel && row.attributeNameHash.includes(`$${dimension.dimensionLabel}$`);
}

/**
 * Splits `"C120  FUL - Operations"` into its code and description on the FIRST run of two or more
 * spaces, so a description containing a wide gap of its own stays intact. A displayName with no such
 * run is a section node: description only, no code.
 */
export function splitSegmentDisplayName(displayName: string | undefined | null): {
    code?: string;
    description?: string;
} {
    // Deliberately NOT trimmed before the split: `"C120  "` is a code with an empty description, and
    // trimming first would collapse it to a description-only section node and make the
    // SEGMENT_DESCRIPTION_UNAVAILABLE fallback unreachable.
    const value = displayName ?? '';
    if (!value.trim()) {
        return {};
    }
    const separator = value.search(/\s{2,}/);
    if (separator === -1) {
        return { description: value.trim() };
    }
    const code = value.slice(0, separator).trim();
    const description = value.slice(separator).trim();
    return {
        code: code || undefined,
        description: description || undefined,
    };
}

/**
 * A value counts as zero only when it is absent/blank or parses to numeric 0. A non-numeric value
 * ("N/A") is NOT zero, so a row carrying one is never hidden by the zero filter.
 */
function isZeroValue(value: string | undefined | null): boolean {
    if (value === undefined || value === null || `${value}`.trim() === '') {
        return true;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed === 0;
}

function isZeroRow(row: TPTaxReportData): boolean {
    return Object.values(row.attributeValue ?? {}).every(isZeroValue);
}

/**
 * Drops segment rows whose whole subtree is zero across every column. Section and account rows are
 * always kept, so totals and the column shape never move; and because a kept non-zero row keeps its
 * ancestors (an ancestor of a non-zero row is itself non-zero in a FRAMES rollup, and is anyway
 * evaluated on its subtree, not on itself alone), hiding never orphans a visible row.
 */
export function filterZeroSegmentRows(
    rows: TPTaxReportData[],
    dimension: LefDrillDownDimension
): TPTaxReportData[] {
    if (isAccountDimension(dimension) || rows.length === 0) {
        return rows;
    }

    const childrenByParent = new Map<string, TPTaxReportData[]>();
    rows.forEach((row) => {
        const parent = row.attributeParentNameHash;
        if (!parent) {
            return;
        }
        const siblings = childrenByParent.get(parent);
        if (siblings) {
            siblings.push(row);
        } else {
            childrenByParent.set(parent, [row]);
        }
    });

    // Memoised post-order walk. The payload is not ordered parent-first, and a malformed parent cycle
    // would otherwise recurse forever, so `visiting` breaks any loop conservatively (treat as non-zero).
    const subtreeIsZero = new Map<string, boolean>();
    const visiting = new Set<string>();
    const isSubtreeZero = (row: TPTaxReportData): boolean => {
        const cached = subtreeIsZero.get(row.attributeNameHash);
        if (cached !== undefined) {
            return cached;
        }
        if (visiting.has(row.attributeNameHash)) {
            return false;
        }
        visiting.add(row.attributeNameHash);
        const result =
            isZeroRow(row) &&
            (childrenByParent.get(row.attributeNameHash) ?? []).every((child) => isSubtreeZero(child));
        visiting.delete(row.attributeNameHash);
        subtreeIsZero.set(row.attributeNameHash, result);
        return result;
    };

    return rows.filter((row) => !isSegmentRow(row, dimension) || !isSubtreeZero(row));
}

export interface ResolvedDimensionView {
    /** False when the report carries no rows for this dimension: render the unavailable state. */
    available: boolean;
    rows: TPTaxReportData[];
}

/**
 * Picks the rows feeding the TP P&L grid for the selected dimension. Account always resolves so the
 * flag-off / default path behaves exactly as today, even for a report with no TP_PNL key at all.
 * A non-Account dimension that is missing OR empty is reported unavailable rather than rendered as an
 * empty grid, which would read as "every segment is zero".
 */
export function resolveDimensionView(
    reportContent: TPTaxReportContent | undefined | null,
    dimension: LefDrillDownDimension
): ResolvedDimensionView {
    const rows = reportContent?.[dimension.reportContentKey];
    if (isAccountDimension(dimension)) {
        return { available: true, rows: rows ?? [] };
    }
    return Array.isArray(rows) && rows.length > 0 ? { available: true, rows } : { available: false, rows: [] };
}

/** Copy for the unavailable state (Story 1.6a). */
export function dimensionUnavailableMessage(dimension: LefDrillDownDimension): string {
    return `${dimension.label} breakdown is not available for this report.`;
}
